<script setup lang="ts">
  import type { EnumValues } from '@travelata-libs/utils';
  import { computed } from 'vue';
  import { TypoVariant } from './Typo.types';

  interface TypoProps {
    variant?: EnumValues<typeof TypoVariant>;
    tag?: 'span' | 'p';
  }

  const props = withDefaults(defineProps<TypoProps>(), {
    variant: TypoVariant.BodyMRegular,
    tag: undefined,
  });

  const tagName = computed(() => {
    if (props.tag) return props.tag;

    switch (props.variant) {
      case TypoVariant.HeadingL1:
      case TypoVariant.Heading1: {
        return 'h1';
      }
      case TypoVariant.HeadingL2:
      case TypoVariant.Heading2: {
        return 'h2';
      }
      case TypoVariant.HeadingL3:
      case TypoVariant.Heading3: {
        return 'h3';
      }
      case TypoVariant.Heading4: {
        return 'h4';
      }
      case TypoVariant.Heading5: {
        return 'h5';
      }
      case TypoVariant.Heading6: {
        return 'h6';
      }
      default: {
        return 'p';
      }
    }
  });
</script>

<template>
  <component :is="tagName" :class="['tl-typo', `tl-typo_${variant}`]">
    <slot />
  </component>
</template>

<style lang="scss" scoped>
  .tl-typo {
    display: block;
    margin: 0;
    padding: 0;

    &_hl {
      &-1 {
        @include typography-hl(1);
      }

      &-2 {
        @include typography-hl(2);
      }

      &-3 {
        @include typography-hl(3);
      }
    }

    &_h {
      &-1 {
        @include typography-h(1);
      }

      &-2 {
        @include typography-h(2);
      }

      &-3 {
        @include typography-h(3);
      }

      &-4 {
        @include typography-h(4);
      }

      &-5 {
        @include typography-h(5);
      }

      &-6 {
        @include typography-h(6, 500);
      }
    }

    &_body {
      &-xs {
        &-400 {
          @include typography-body(xs, 400);

          &-short {
            @include typography-body(xs, 400, true);
          }
        }

        &-500 {
          @include typography-body(xs, 500);

          &-short {
            @include typography-body(xs, 500, true);
          }
        }

        &-700 {
          @include typography-body(xs, 700);

          &-short {
            @include typography-body(xs, 700, true);
          }
        }
      }

      &-s {
        &-400 {
          @include typography-body(s, 400);

          &-short {
            @include typography-body(s, 400, true);
          }
        }

        &-500 {
          @include typography-body(s, 500);

          &-short {
            @include typography-body(s, 500, true);
          }
        }

        &-700 {
          @include typography-body(s, 700);

          &-short {
            @include typography-body(s, 700, true);
          }
        }
      }

      &-m {
        &-400 {
          @include typography-body(m, 400);

          &-short {
            @include typography-body(m, 400, true);
          }
        }

        &-500 {
          @include typography-body(m, 500);

          &-short {
            @include typography-body(m, 500, true);
          }
        }

        &-700 {
          @include typography-body(m, 700);

          &-short {
            @include typography-body(m, 700, true);
          }
        }
      }

      &-l {
        &-400 {
          @include typography-body(l, 400);

          &-short {
            @include typography-body(l, 400, true);
          }
        }

        &-500 {
          @include typography-body(l, 500);

          &-short {
            @include typography-body(l, 500, true);
          }
        }

        &-700 {
          @include typography-body(l, 700);

          &-short {
            @include typography-body(l, 700, true);
          }
        }
      }

      &-xl {
        &-400 {
          @include typography-body(xl, 400);

          &-short {
            @include typography-body(xl, 400, true);
          }
        }

        &-500 {
          @include typography-body(xl, 500);

          &-short {
            @include typography-body(xl, 500, true);
          }
        }

        &-700 {
          @include typography-body(xl, 700);

          &-short {
            @include typography-body(xl, 700, true);
          }
        }
      }
    }

    &_caption {
      &-s {
        &-400 {
          @include typography-caption(s, 400);

          &-short {
            @include typography-caption(s, 400, true);
          }
        }

        &-700 {
          @include typography-caption(s, 700);

          &-short {
            @include typography-caption(s, 700, true);
          }
        }
      }

      &-m {
        &-400 {
          @include typography-caption(m, 400);

          &-short {
            @include typography-caption(m, 400, true);
          }
        }

        &-700 {
          @include typography-caption(m, 700);

          &-short {
            @include typography-caption(m, 700, true);
          }
        }
      }
    }
  }
</style>
